import api from './api';

// Billing types
export interface BillingMethod {
  id: string;
  name: string;
  enabled: boolean;
}

export interface BillingSummary {
  company_id: number;
  total_devices: number;
  billable_devices: number;
  monthly_cost: number;
  billing_method?: string;
}

export interface MrrHistoryItem {
  month: string;
  mrr: number;
  companies: number;
}

export interface BillingSettings {
  company_id: number;
  billing_method?: string;
  odoo_company_id?: string;
  odoo_contact_id?: string;
  stripe_customer_id?: string;
  billing_enabled: boolean;
}

export interface ConnectionTestResponse {
  success: boolean;
  message: string;
}

export interface SyncResponse {
  success: boolean;
  synced: number;
  message: string;
}

export interface SyncLog {
  id: number;
  company_id: number;
  status: 'success' | 'error';
  devices_billed: number;
  message?: string;
  created_at: string;
}

export interface StripePrice {
  id: string;
  product: string;
  unit_amount: number;
  currency: string;
}

export interface StripeStatus {
  enabled: boolean;
  customer_id?: string;
  subscription_id?: string;
  status?: string;
}

export interface StripePortalResponse {
  url: string;
}

export interface SyncLogsOptions {
  companyId?: number;
  limit?: number;
}

// Payment methods (card on file) — company admin self-serve
export interface SavedPaymentMethod {
  id: string;
  brand?: string;
  last4?: string;
  expMonth?: number;
  expYear?: number;
  createdAt?: string | null;
}

export interface PaymentMethodsResponse {
  configured: boolean;
  methods: SavedPaymentMethod[];
  defaultPaymentMethodId: string | null;
  // Billing gate (migration 051): gated = the company's API keys 403 until a
  // card is added; exempt companies (grandfathered / super-admin flag) never gate.
  apiAccess?: {
    gated: boolean;
    exempt: boolean;
    hasPaymentMethod: boolean;
  };
}

export interface InvoiceItem {
  id: string;
  number?: string | null;
  status: string;
  total: number;      // in the currency's minor unit (cents)
  currency: string;
  created: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
}

export interface InvoicesResponse {
  configured: boolean;
  invoices: InvoiceItem[];
  upcoming: { amountDue: number; currency: string; periodEnd: string | null } | null;
}

export async function getInvoices(companyId: string | number): Promise<InvoicesResponse> {
  const response = await api.get<InvoicesResponse>(`/billing/invoices/${companyId}`);
  return response.data;
}

// Recurring service billing (migration 056): one statement per month covering
// deployed order-registered devices at their order-line fee snapshots.
export interface ServiceStatementLine {
  id: number;
  manufacturer_id: number;
  manufacturer_name: string;
  description: string | null;
  device_count: number;
  monthly_fee_cents: number;
  amount_cents: number;
}

export interface ServiceStatement {
  id: number;
  period_start: string;
  period_end: string;
  currency: string;
  total_cents: number;
  platform_fee_cents: number;
  status: 'invoiced' | 'paid' | 'void';
  charge_failure_reason: string | null;
  created_at: string;
  lines: ServiceStatementLine[];
}

export async function getServiceStatements(companyId: string | number): Promise<{ statements: ServiceStatement[] }> {
  const response = await api.get(`/billing/service-statements/${companyId}`);
  return response.data;
}

export async function getPaymentMethods(companyId: string | number): Promise<PaymentMethodsResponse> {
  const response = await api.get<PaymentMethodsResponse>(`/billing/payment-methods/${companyId}`);
  return response.data;
}

export async function createCardSetupIntent(companyId: string | number): Promise<{ clientSecret: string; publishableKey: string }> {
  const response = await api.post(`/billing/payment-methods/${companyId}/setup-intent`);
  return response.data;
}

export async function setDefaultPaymentMethod(companyId: string | number, paymentMethodId: string): Promise<{ success: boolean }> {
  const response = await api.put(`/billing/payment-methods/${companyId}/default`, { paymentMethodId });
  return response.data;
}

export async function removePaymentMethod(companyId: string | number, paymentMethodId: string): Promise<{ success: boolean }> {
  const response = await api.delete(`/billing/payment-methods/${companyId}/${paymentMethodId}`);
  return response.data;
}

// General Billing
export async function getBillingMethods(): Promise<BillingMethod[]> {
  const response = await api.get<BillingMethod[]>('/billing/methods');
  return response.data;
}

export async function getBillingSummary(companyId?: number): Promise<BillingSummary> {
  const url = companyId ? `/billing/summary/${companyId}` : '/billing/summary';
  const response = await api.get<BillingSummary>(url);
  return response.data;
}

export async function getAllBilling(): Promise<BillingSummary[]> {
  const response = await api.get<BillingSummary[]>('/billing/all');
  return response.data;
}

export async function getMrrHistory(months = 12): Promise<MrrHistoryItem[]> {
  const response = await api.get<MrrHistoryItem[]>('/billing/mrr-history', { params: { months } });
  return response.data;
}

export async function getBillingSettings(companyId: number): Promise<BillingSettings> {
  const response = await api.get<BillingSettings>(`/billing/settings/${companyId}`);
  return response.data;
}

export async function updateBillingSettings(companyId: number, settings: Partial<BillingSettings>): Promise<BillingSettings> {
  const response = await api.put<BillingSettings>(`/billing/settings/${companyId}`, settings);
  return response.data;
}

export async function setBillingMethod(companyId: number, method: string): Promise<BillingSettings> {
  const response = await api.put<BillingSettings>(`/billing/method/${companyId}`, { method });
  return response.data;
}

export async function syncCompany(companyId: number): Promise<SyncResponse> {
  const response = await api.post<SyncResponse>(`/billing/sync/${companyId}`);
  return response.data;
}

export async function syncAll(): Promise<SyncResponse> {
  const response = await api.post<SyncResponse>('/billing/sync');
  return response.data;
}

// Odoo
export async function testOdooConnection(): Promise<ConnectionTestResponse> {
  const response = await api.get<ConnectionTestResponse>('/billing/odoo/test');
  return response.data;
}

export async function syncAllToOdoo(): Promise<SyncResponse> {
  const response = await api.post<SyncResponse>('/billing/odoo/sync');
  return response.data;
}

export async function getOdooSyncLogs(options: SyncLogsOptions = {}): Promise<SyncLog[]> {
  const params = new URLSearchParams();
  if (options.companyId) params.append('companyId', options.companyId.toString());
  if (options.limit) params.append('limit', options.limit.toString());

  const response = await api.get<SyncLog[]>(`/billing/odoo/logs?${params.toString()}`);
  return response.data;
}

// Stripe
export async function testStripeConnection(): Promise<ConnectionTestResponse> {
  const response = await api.get<ConnectionTestResponse>('/billing/stripe/test');
  return response.data;
}

export async function syncAllToStripe(): Promise<SyncResponse> {
  const response = await api.post<SyncResponse>('/billing/stripe/sync');
  return response.data;
}

export async function setupStripeBilling(companyId: number, options: Record<string, any> = {}): Promise<StripeStatus> {
  const response = await api.post<StripeStatus>(`/billing/stripe/setup/${companyId}`, options);
  return response.data;
}

export async function getStripeStatus(companyId: number): Promise<StripeStatus> {
  const response = await api.get<StripeStatus>(`/billing/stripe/status/${companyId}`);
  return response.data;
}

export async function getStripeSyncLogs(companyId: number, options: SyncLogsOptions = {}): Promise<SyncLog[]> {
  const params = new URLSearchParams();
  if (options.limit) params.append('limit', options.limit.toString());

  const response = await api.get<SyncLog[]>(`/billing/stripe/logs/${companyId}?${params.toString()}`);
  return response.data;
}

export async function getStripePrices(): Promise<StripePrice[]> {
  const response = await api.get<StripePrice[]>('/billing/stripe/prices');
  return response.data;
}

export async function getStripePortalUrl(companyId: number, returnUrl?: string): Promise<StripePortalResponse> {
  const params = returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : '';
  const response = await api.get<StripePortalResponse>(`/billing/stripe/portal/${companyId}${params}`);
  return response.data;
}

// Legacy (backwards compatibility)
export async function getSyncLogs(options: SyncLogsOptions = {}): Promise<SyncLog[]> {
  const params = new URLSearchParams();
  if (options.companyId) params.append('companyId', options.companyId.toString());
  if (options.limit) params.append('limit', options.limit.toString());

  const response = await api.get<SyncLog[]>(`/billing/sync/logs?${params.toString()}`);
  return response.data;
}

// Alias for backwards compatibility
export const syncCompanyToOdoo = syncCompany;
